
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface CreditUsage {
  credits_input: number;
  credits_output: number;
  total_credits: number;
  limit_credits: number;
  percentage: number;
  purchased_credits?: number;
  purchased_credits_details?: Array<{
    id: string;
    credits_remaining: number;
    expires_at: string;
    purchased_at: string;
  }>;
}

export const useCreditsUsage = () => {
  const { user } = useAuth();
  const [creditUsage, setCreditUsage] = useState<CreditUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditUsage = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
      
      // Buscar dados de créditos do usuário
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('credits_input, credits_output, total_credits, limit_credits')
        .eq('user_id', user.id)
        .eq('month_year', currentMonth)
        .maybeSingle();

      if (creditsError) {
        throw creditsError;
      }

      // Buscar créditos comprados disponíveis
      const { data: purchasedData, error: purchasedError } = await supabase
        .rpc('get_available_purchased_credits', { p_user_id: user.id });

      if (purchasedError) {
        console.error('Error fetching purchased credits:', purchasedError);
      }

      const purchasedCredits = purchasedData?.[0]?.total_available_credits || 0;
      const purchasedDetails = (purchasedData?.[0]?.credits_details as any) || [];

      // Se não há dados de créditos, buscar o limite baseado na assinatura
      let limit_credits = 10000; // Default free plan
      
      if (!creditsData) {
        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('plan_type')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        // Definir limite baseado no plano
        if (subscription) {
          switch (subscription.plan_type) {
            case 'premium':
              limit_credits = 2000000; // 2M
              break;
            case 'pro':
              limit_credits = 1000000; // 1M
              break;
            case 'basic':
              limit_credits = 500000; // 500K
              break;
            case 'free':
            default:
              limit_credits = 10000; // 10K
              break;
          }
        }
      }

      if (creditsData) {
        // Limite efetivo = limite do plano + créditos comprados disponíveis
        const effectiveLimit = creditsData.limit_credits + purchasedCredits;
        const percentage = Math.min((creditsData.total_credits / effectiveLimit) * 100, 100);
        
        setCreditUsage({
          credits_input: creditsData.credits_input,
          credits_output: creditsData.credits_output,
          total_credits: creditsData.total_credits,
          limit_credits: effectiveLimit,
          percentage: Math.round(percentage * 100) / 100,
          purchased_credits: purchasedCredits,
          purchased_credits_details: purchasedDetails
        });
      } else {
        // No usage yet this month, create with correct limit
        setCreditUsage({
          credits_input: 0,
          credits_output: 0,
          total_credits: 0,
          limit_credits: limit_credits + purchasedCredits,
          percentage: 0,
          purchased_credits: purchasedCredits,
          purchased_credits_details: purchasedDetails
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching credit usage:', err);
      setError(err instanceof Error ? err.message : 'Error fetching credit usage');
    } finally {
      setLoading(false);
    }
  };

  const formatCredits = (credits: number): string => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    }
    return credits.toString();
  };

  const isLimitReached = (): boolean => {
    return creditUsage ? creditUsage.total_credits >= creditUsage.limit_credits : false;
  };

  const getRemainingCredits = (): number => {
    return creditUsage ? Math.max(0, creditUsage.limit_credits - creditUsage.total_credits) : 10000;
  };

  useEffect(() => {
    fetchCreditUsage();
  }, [user]);

  return {
    creditUsage,
    loading,
    error,
    formatCredits,
    isLimitReached,
    getRemainingCredits,
    refetch: fetchCreditUsage
  };
};
